/**
 * « Pour réviser » — écran d'accueil d'un exercice (player V2), juste avant
 * « Commencer ». Les SKILLCARDS reliées à l'exercice (une pastille par carte :
 * créature + titre → fenêtre Cours) et SEULEMENT les outils que leurs auteurs
 * y ont reliés (`card_tools`, jamais les suggestions de la boîte) → l'outil
 * s'ouvre directement. Rien n'est affiché si l'exercice n'a aucune carte.
 */

import { useMemo, useState } from 'react';
import { MathText } from '@jeraisonne/ui';
import { OrigamiCreature } from '@jeraisonne/ui/tool-cards';
import { cardPalette } from '@jeraisonne/db/cards';
import { useExerciseCardLinks, type ExerciseCardLink } from '@/lib/cards/student-skillcards';
import { usePublicCards } from '@/lib/cards/api';
import { CardLessonPopup } from '@/features/skillcards/card-lesson-popup';
import {
  ToolChips,
  ToolboxSheet,
  contextForCards,
  linkedTools,
  type ToolDef,
  type ToolId,
} from '@/features/toolbox';

/** Une carte par SKILLCARD, dans l'ordre des questions. PUR. */
export function uniqueExerciseCards(links: readonly ExerciseCardLink[]): ExerciseCardLink[] {
  const seen = new Set<string>();
  const out: ExerciseCardLink[] = [];
  for (const l of links) {
    if (seen.has(l.card_id)) continue;
    seen.add(l.card_id);
    out.push(l);
  }
  return out;
}

/** Outils reliés à au moins une des cartes, sans doublon, dans l'ordre des cartes. PUR. */
export function linkedToolsOfCards(toolIdsByCard: readonly (readonly string[])[]): ToolDef[] {
  return linkedTools(toolIdsByCard.flat());
}

export function IntroRevision({
  exerciseId,
  level,
}: {
  exerciseId: string;
  /** niveau de l'exercice (choix de la version d'un outil) */
  level?: string | null;
}) {
  const { data: links } = useExerciseCardLinks(exerciseId);
  const cards = useMemo(() => uniqueExerciseCards(links ?? []), [links]);
  const cardIds = useMemo(() => cards.map((c) => c.card_id), [cards]);
  const publicCards = usePublicCards(cardIds);
  const tools = useMemo(
    () => linkedToolsOfCards(publicCards.map((p) => p.tools ?? [])),
    [publicCards],
  );
  const context = useMemo(
    () =>
      contextForCards(
        publicCards.map((p) => ({
          domain_label: p.card.domain_label,
          topic: p.card.topic,
          title: p.card.title,
          tools: p.tools,
        })),
        level ?? cards[0]?.levels[0] ?? null,
      ),
    [publicCards, level, cards],
  );
  const [lessonCard, setLessonCard] = useState<string | null>(null);
  const [tool, setTool] = useState<ToolId | null>(null);

  if (cards.length === 0) return null;
  const lessonCards = lessonCard
    ? [
        ...cards.filter((c) => c.card_id === lessonCard),
        ...cards.filter((c) => c.card_id !== lessonCard),
      ]
    : [];

  return (
    <section
      aria-label="Pour réviser avant de commencer"
      data-testid="intro-revision"
      className="border-ink-200 space-y-3 rounded-2xl border bg-white p-4 sm:p-5"
    >
      <p className="font-display text-ink-500 text-[11px] font-semibold uppercase tracking-widest">
        Pour réviser avant de commencer
      </p>
      <ul className="flex flex-wrap gap-2">
        {cards.map((c) => {
          const palette = cardPalette(c.levels, c.color_theme);
          return (
            <li key={c.card_id}>
              <button
                type="button"
                onClick={() => setLessonCard(c.card_id)}
                className="inline-flex min-h-11 max-w-full items-center gap-2 rounded-full py-1 pl-1 pr-3 text-left text-sm font-semibold text-[#ffffff] active:scale-[0.98]"
                style={{ background: palette.bg, boxShadow: `inset 0 0 0 1.5px ${palette.accent}` }}
              >
                <span
                  aria-hidden
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
                  style={{ boxShadow: `inset 0 0 0 1.5px ${palette.accent}` }}
                >
                  <OrigamiCreature
                    creature={c.creature}
                    accent={palette.accent}
                    className="h-7 w-7"
                  />
                </span>
                <span className="min-w-0 truncate">
                  <MathText>{c.title}</MathText>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {tools.length > 0 && (
        <div className="space-y-2">
          <p className="text-ink-500 font-sans text-xs font-semibold">Outils</p>
          <ToolChips tools={tools} onOpen={setTool} />
        </div>
      )}

      {lessonCard && (
        <CardLessonPopup
          cards={lessonCards}
          onClose={() => setLessonCard(null)}
          onOpenTool={(id) => {
            setLessonCard(null);
            setTool(id);
          }}
        />
      )}
      {tool && (
        <ToolboxSheet
          context={context}
          initialTool={tool}
          featuredTitle="Pour cet exercice"
          onClose={() => setTool(null)}
        />
      )}
    </section>
  );
}
